import { z } from 'zod';
import { COUNTRY_CODES } from './countries';

/**
 * The waitlist contract, shared by the forms and the same-origin routes.
 *
 * Mirrors the gateway's DTOs (docs/waitlist/api-contract.md): the gateway is
 * the authority and re-validates everything, so these rules exist to give
 * people immediate, specific feedback and to stop obviously bad requests at
 * the edge. Only the email, a country and at least one role are required.
 */

export const WAITLIST_ROLE_IDS = ['CUSTOMER', 'RIDE_DRIVER', 'DELIVERY_RIDER', 'TOW_OPERATOR', 'CARGO_DRIVER', 'CURIOUS'] as const;
export type WaitlistRoleId = (typeof WAITLIST_ROLE_IDS)[number];

export const ROLE_LABELS: Record<WaitlistRoleId, string> = {
  CUSTOMER: 'Customer',
  RIDE_DRIVER: 'Ride driver',
  DELIVERY_RIDER: 'Delivery rider',
  TOW_OPERATOR: 'Tow operator',
  CARGO_DRIVER: 'Cargo driver',
  CURIOUS: 'Just curious',
};

/** Roles that work through the operator app. */
export const OPERATOR_ROLE_IDS: readonly WaitlistRoleId[] = ['RIDE_DRIVER', 'DELIVERY_RIDER', 'TOW_OPERATOR', 'CARGO_DRIVER'];

/**
 * The labels the website used before roles had ids. `/waitlist?role=Rider`
 * links are already out in the world (shared messages, old `/earn` pages), so
 * they keep working. "Rider" was always the delivery rider.
 */
const LEGACY_ROLE_LABELS: Record<string, WaitlistRoleId> = {
  customer: 'CUSTOMER',
  driver: 'RIDE_DRIVER',
  rider: 'DELIVERY_RIDER',
  'tow operator': 'TOW_OPERATOR',
  'cargo driver': 'CARGO_DRIVER',
  'just curious': 'CURIOUS',
};

/** A role id from `?role=` (new id or legacy label), or undefined for anything else. */
export function roleFromParam(value: unknown): WaitlistRoleId | undefined {
  if (typeof value !== 'string' || value.length > 40) return undefined;
  const v = value.trim();
  if ((WAITLIST_ROLE_IDS as readonly string[]).includes(v.toUpperCase())) return v.toUpperCase() as WaitlistRoleId;
  return LEGACY_ROLE_LABELS[v.toLowerCase()];
}

export const WAITLIST_SERVICES = ['RIDES', 'DELIVERY', 'TOWING', 'CARGO'] as const;
export type WaitlistService = (typeof WAITLIST_SERVICES)[number];

export const SERVICE_LABELS: Record<WaitlistService, string> = {
  RIDES: 'Rides',
  DELIVERY: 'Deliveries',
  TOWING: 'Towing',
  CARGO: 'Moving cargo',
};

/** Nigerian states and the FCT, in the gateway's codes, with the names people use. */
export const NG_STATES = {
  ABIA: 'Abia',
  ADAMAWA: 'Adamawa',
  AKWA_IBOM: 'Akwa Ibom',
  ANAMBRA: 'Anambra',
  BAUCHI: 'Bauchi',
  BAYELSA: 'Bayelsa',
  BENUE: 'Benue',
  BORNO: 'Borno',
  CROSS_RIVER: 'Cross River',
  DELTA: 'Delta',
  EBONYI: 'Ebonyi',
  EDO: 'Edo',
  EKITI: 'Ekiti',
  ENUGU: 'Enugu',
  FCT: 'Federal Capital Territory (Abuja)',
  GOMBE: 'Gombe',
  IMO: 'Imo',
  JIGAWA: 'Jigawa',
  KADUNA: 'Kaduna',
  KANO: 'Kano',
  KATSINA: 'Katsina',
  KEBBI: 'Kebbi',
  KOGI: 'Kogi',
  KWARA: 'Kwara',
  LAGOS: 'Lagos',
  NASARAWA: 'Nasarawa',
  NIGER: 'Niger',
  OGUN: 'Ogun',
  ONDO: 'Ondo',
  OSUN: 'Osun',
  OYO: 'Oyo',
  PLATEAU: 'Plateau',
  RIVERS: 'Rivers',
  SOKOTO: 'Sokoto',
  TARABA: 'Taraba',
  YOBE: 'Yobe',
  ZAMFARA: 'Zamfara',
} as const;
export type NgState = keyof typeof NG_STATES;
export const NG_STATE_CODES = Object.keys(NG_STATES) as [NgState, ...NgState[]];

/** Suggestions only: anyone can type a city that is not here. */
export const NG_CITY_SUGGESTIONS = ['Lagos', 'Abuja', 'Port Harcourt', 'Ibadan', 'Kano', 'Benin City', 'Enugu', 'Kaduna', 'Abeokuta', 'Uyo', 'Warri', 'Ilorin'];

export const DEFAULT_COUNTRY = 'NG';

/** Gateway token shape: base64url, 20–200 characters. */
export const TOKEN_RE = /^[A-Za-z0-9_-]{20,200}$/;

/** Join and PATCH bodies are well under 1 KB; anything near this is not a person. */
export const MAX_BODY_BYTES = 2_048;
/** Bodies that carry one email or one token. */
export const MAX_SMALL_BODY_BYTES = 512;

/**
 * Free text (a name or a city) in any script. Only what could be markup, a
 * link or an address is refused, matching the gateway: `<>{}`, `@`, `://` and
 * control characters. "Ọlá Adébáyọ̀", "李雷" and "O'Brien-Nnamdi" are all fine.
 */
const UNSAFE_TEXT = /[<>{}@\u0000-\u001f\u007f-\u009f]|:\/\//;

const freeText = (label: string) =>
  z
    .string()
    .trim()
    .max(80, `${label} must be 80 characters or fewer.`)
    .refine((v) => !UNSAFE_TEXT.test(v), `${label} can't include links, email addresses or the characters < > { } @.`);

const email = z
  .string({ required_error: 'Enter your email address.' })
  .trim()
  .min(1, 'Enter your email address.')
  .max(254, 'Email must be 254 characters or fewer.')
  .email('Enter an email address like name@example.com.')
  // Lower-cased so the same person is not listed twice. Mailbox local parts
  // are technically case-sensitive; no real provider treats them so.
  .transform((v) => v.toLowerCase());

const countryCode = z.enum(COUNTRY_CODES, { errorMap: () => ({ message: 'Choose your country.' }) });
const region = z.enum(NG_STATE_CODES, { errorMap: () => ({ message: 'Choose your state.' }) });

const roles = z
  .array(z.enum(WAITLIST_ROLE_IDS), { errorMap: () => ({ message: 'Choose at least one option.' }) })
  .min(1, 'Choose at least one option.')
  .max(WAITLIST_ROLE_IDS.length)
  .refine((r) => new Set(r).size === r.length, 'Choose each option once.')
  .refine((r) => !r.includes('CURIOUS') || r.length === 1, '"Just curious" can\'t be combined with other options.');

const services = z
  .array(z.enum(WAITLIST_SERVICES), { errorMap: () => ({ message: 'Choose from the listed services.' }) })
  .max(WAITLIST_SERVICES.length)
  .refine((s) => new Set(s).size === s.length, 'Choose each service once.');

/** Blank optional text becomes absent, so the gateway never stores "". */
const blankToUndefined = (v: unknown) => (typeof v === 'string' && v.trim() === '' ? undefined : v);

/** Honeypot: hidden from people, filled by naive bots. Must be empty. */
const honeypot = z.string().max(0).optional();

export const joinSchema = z
  .object({
    email,
    displayName: z.preprocess(blankToUndefined, freeText('Name').optional()),
    countryCode,
    region: z.preprocess(blankToUndefined, region.optional()),
    city: z.preprocess(blankToUndefined, freeText('City').optional()),
    roles,
    services: services.optional(),
    marketing: z.boolean({ errorMap: () => ({ message: 'Choose whether you want product updates.' }) }),
    website: honeypot,
  })
  .strict()
  .superRefine((v, ctx) => {
    if (v.countryCode === 'NG' && !v.region) ctx.addIssue({ code: 'custom', path: ['region'], message: 'Choose your state.' });
    if (v.countryCode !== 'NG' && v.region) ctx.addIssue({ code: 'custom', path: ['region'], message: 'A state applies to Nigeria only.' });
  });
export type JoinInput = z.infer<typeof joinSchema>;

/**
 * Zod skips an object's refinements while any field is invalid, so a missing
 * Nigerian state would only be reported after everything else was fixed. The
 * forms call this to list every problem at once.
 */
export function joinFieldErrors(input: { countryCode?: string; region?: string }, error: z.ZodError): WaitlistFieldErrors {
  const errs = fieldErrors(error);
  if (!errs.region && input.countryCode === 'NG' && !input.region) errs.region = 'Choose your state.';
  return errs;
}

export const emailRequestSchema = z.object({ email, website: honeypot }).strict();

export const tokenSchema = z
  .object({ token: z.string().regex(TOKEN_RE, 'That link is not valid.') })
  .strict();

/** Blank optional text clears the stored value. */
const blankToNull = (v: unknown) => (typeof v === 'string' && v.trim() === '' ? null : v);

export const updateSchema = z
  .object({
    displayName: z.preprocess(blankToNull, freeText('Name').nullable().optional()),
    countryCode: countryCode.optional(),
    region: z.preprocess(blankToNull, region.nullable().optional()),
    city: z.preprocess(blankToNull, freeText('City').nullable().optional()),
    roles: roles.optional(),
    services: services.optional(),
    marketing: z.boolean().optional(),
    rejoin: z.literal(true).optional(),
  })
  .strict()
  .superRefine((v, ctx) => {
    if (v.countryCode === 'NG' && v.region === null) ctx.addIssue({ code: 'custom', path: ['region'], message: 'Choose your state.' });
    if (v.countryCode && v.countryCode !== 'NG' && v.region) {
      ctx.addIssue({ code: 'custom', path: ['region'], message: 'A state applies to Nigeria only.' });
    }
  });
export type UpdateInput = z.infer<typeof updateSchema>;

export const FIELD_NAMES = ['email', 'displayName', 'countryCode', 'region', 'city', 'roles', 'services', 'marketing'] as const;
export type WaitlistField = (typeof FIELD_NAMES)[number];
export type WaitlistFieldErrors = Partial<Record<WaitlistField, string>>;

/** Friendly copy for a field the gateway refused, without echoing its wording. */
export const FIELD_MESSAGES: Record<WaitlistField, string> = {
  email: 'Enter an email address like name@example.com.',
  displayName: "Check your name: up to 80 characters, with no links or the characters < > { } @.",
  countryCode: 'Choose your country.',
  region: 'Choose your state.',
  city: "Check your city: up to 80 characters, with no links or the characters < > { } @.",
  roles: 'Choose at least one option.',
  services: 'Choose from the listed services.',
  marketing: 'Choose whether you want product updates.',
};

export function fieldErrors(error: z.ZodError): WaitlistFieldErrors {
  const out: WaitlistFieldErrors = {};
  for (const issue of error.issues) {
    const key = issue.path[0] as WaitlistField;
    if ((FIELD_NAMES as readonly string[]).includes(key) && !out[key]) out[key] = issue.message;
  }
  return out;
}

/** The shape `GET /waitlist/me` returns. */
export interface WaitlistProfile {
  email: string;
  displayName: string | null;
  countryCode: string | null;
  region: string | null;
  city: string | null;
  roles: WaitlistRoleId[];
  services: WaitlistService[];
  marketing: boolean;
  membership: 'ACTIVE' | 'LEFT';
  emailVerified: boolean;
  joinedAt: string;
}

export interface WaitlistSession {
  token: string;
  expiresAt: string;
}
